import { makeRng } from '../lib/rng.js';
function hash(s) {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++)
        h = Math.imul(h ^ s.charCodeAt(i), 16777619);
    return h >>> 0;
}
/**
 * Deterministic fake transport. Each file gets its own duration and easing seeded
 * from its name, so batches finish out of order the way real uploads do, but the
 * same batch always behaves the same way.
 */
export function createSimulatedUploader(opts = {}) {
    const { speed = 1, videoProcessingMs = 900, failWhen } = opts;
    const attempts = opts.attempts ?? new Map();
    return (file, { signal, onProgress, onProcessing }) => new Promise((resolve, reject) => {
        const attempt = (attempts.get(file.name) ?? 0) + 1;
        attempts.set(file.name, attempt);
        const rng = makeRng(hash(file.name) + attempt);
        // 1.5–4s scaled by size a little, so a 60MB TIFF visibly outlasts an 8MB PNG.
        const sizeMB = file.size / 1048576;
        const base = 1500 + rng() * 1200 + Math.min(sizeMB, 80) * 18;
        const duration = (base / speed) | 0;
        const stallAt = 0.3 + rng() * 0.5; // every transfer has one hitch
        const stallLen = 0.06 + rng() * 0.08;
        let raf = 0;
        const t0 = performance.now();
        const abort = () => {
            cancelAnimationFrame(raf);
            reject(new DOMException('Upload cancelled', 'AbortError'));
        };
        if (signal.aborted)
            return abort();
        signal.addEventListener('abort', abort, { once: true });
        const finish = () => {
            signal.removeEventListener('abort', abort);
            if (failWhen?.(file, attempt)) {
                reject(new Error('Network error while finalising upload'));
                return;
            }
            resolve({ url: `https://cdn.arthelper.test/${encodeURIComponent(file.name)}`, attempt });
        };
        const tick = (now) => {
            const u = Math.min(1, (now - t0) / duration);
            // ease-in-out with a stall plateau
            let p = u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2;
            if (u > stallAt && u < stallAt + stallLen) {
                const s = stallAt < 0.5 ? 2 * stallAt * stallAt : 1 - Math.pow(-2 * stallAt + 2, 2) / 2;
                p = s;
            }
            onProgress(p);
            if (u < 1) {
                raf = requestAnimationFrame(tick);
                return;
            }
            if (file.type.startsWith('video/')) {
                onProcessing();
                const id = setTimeout(finish, videoProcessingMs / speed);
                signal.addEventListener('abort', () => clearTimeout(id), { once: true });
            }
            else {
                finish();
            }
        };
        raf = requestAnimationFrame(tick);
    });
}
export const simulatedUploader = createSimulatedUploader();
//# sourceMappingURL=uploader.js.map